import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, map } from 'rxjs';
import { APIResponse } from '../../models/APIResponse';
import { environment } from '../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { EMVViscosityData, EmvOutOfRangeState, EmvValidationResult, SlopeInterceptDto, ViscosityData } from '../models/viscosity';
import { Chart, ChartDataset, ScriptableContext, ScriptableLineSegmentContext } from 'chart.js';
import { StatusParams } from '../../msst/models/msst';
import { SampleCreationModel } from '../../models/sample';
import { CVD_CONSTANTS as CC, PARAFFIN_CONSTANTS as PC } from '../../common/constants';
import { API_ENDPOINTS as AE, API_ENDPOINTS, REGEX_CONSTANTS as RC, VISCOSITY_CONSTANTS as VC } from '../../common/experiment-constants';
import { NUMBERS_CONSTANTS as NC } from '../../common/numbers-constants';
import { MESSAGES_CONSTANTS as MC } from '../../common/messages-constants';
import { ToastrService } from 'ngx-toastr';
import { apiV1 } from '../../common/texts-constants';

@Injectable({
  providedIn: 'root'
})
export class ViscosityService {
  private _http = inject(HttpClient);
  private toastr = inject(ToastrService);


  private apiBaseUrl = environment.apiUrl + apiV1;
  private viscosityDatasource = new BehaviorSubject<ViscosityData[]>([]);
  public statusChange$ = new BehaviorSubject<StatusParams>({ statusId: NC.zero, statusName: '' });

  SaveViscosity(viscosityData: EMVViscosityData) {
    return this._http.post<APIResponse<unknown>>(this.apiBaseUrl + AE.SAVE_VISCOSITY, viscosityData);
  }

  GeViscosity(testRepeatId: number) {
    return this._http.get<APIResponse<EMVViscosityData>>(`${this.apiBaseUrl}${AE.GET_VISCOSITY}?${PC.testRepeatId}=${testRepeatId}`);
  }

  GetSlopeIntercept(viscosityData: EMVViscosityData) {
    return this._http.post<APIResponse<SlopeInterceptDto>>(
      this.apiBaseUrl + AE.GET_SLOPE_INTERCEPT, viscosityData);
  }

  UpdateViscosity(viscosityData: EMVViscosityData) {
    return this._http.post<APIResponse<unknown>>(this.apiBaseUrl + AE.UPDATE_VISCOSITY, viscosityData);
  }

  // Update Viscosity Data Source
  UpdateExcelViscosityData(newData: ViscosityData[]) {
    this.viscosityDatasource.next(newData);
  }

  CreateResultsViscosityReportGraph(ctx: CanvasRenderingContext2D, emvViscosityData: EMVViscosityData): Chart {
    const allPoints = emvViscosityData.viscosityData
      .filter(d => d.pressure !== null)
      .map(d => ({ x: d.pressure!, y: d.pcv ?? NC.zero }));

    // dynamic axis ranges
    
    const maxValueX = Math.max(...allPoints.map(item => item.x));
    const maxValueY = Math.max(...allPoints.map(item => item.y));
    const xStepSize = 2000;
    const yStepSize = 
        maxValueY <= 1 ? 0.1 :
        maxValueY <= 2 ? 0.2 :
        maxValueY <= 4 ? 0.5 : 1;
    const xMin = 0;
    const yMin = (yStepSize == 0.1 || yStepSize == 1) ? 0 : 0.2;
    const xMax = (Math.ceil(maxValueX / xStepSize) * xStepSize)+xStepSize;
    let yMax = Math.ceil(maxValueY / yStepSize) * yStepSize;
    if(yStepSize>=0.5)
    yMax =  Math.ceil((maxValueY - yMin) / yStepSize) * yStepSize +  yMin +  yStepSize;
    
    return new Chart(ctx, {
      type: 'line',
      data: {
        datasets: [
          {
            data: allPoints,
            borderColor: '#5B9BD5',
            backgroundColor: 'rgba(91, 155, 213, 0.2)',
            fill: false,
            pointStyle: 'circle',
            pointRadius: NC.eleven,
            tension: NC.pointthree,
            borderWidth: NC.four,
            segment: {
              borderColor: 'black',
              borderWidth: NC.four,
            },
            pointBackgroundColor: (element: ScriptableContext<'line'>) => {
              const index = element.dataIndex;
              const pressure = allPoints[index]?.x;
              if (pressure === emvViscosityData.satP) {
                return '#C00000';
              }
              else if (pressure === emvViscosityData.resP) {
                return '#00B050';
              }
              else {
                return '#5B9BD5';
              }
            },
            pointBorderColor: 'black'
          }
        ]
      },
      options: {
        responsive: true,
        plugins: {
          legend: { display: false },
          title: { display: false },
          datalabels: { display: false },

        },
        scales: {
          x: {
            type: 'linear',
            border: {
              display: true,
              color: 'black',
              width: NC.three
            },
            title: {
              display: true, text: CC.pressurePsia, font: {
                size: NC.thirtyFive,
                weight: 'bold'
              },
              color: 'black'
            },
            grid: {
              display: false,
            },
            ticks: {
              font: {
                weight: 'bold',
                size: NC.thirty
              },
              color: 'black',
              stepSize:xStepSize,
              // Ensure no decimals on the X-axis ticks
              //callback: (value) => Number(value).toLocaleString('en-US', { maximumFractionDigits: NC.zero })
              callback: (value) => value.toString()
            },
            min: xMin,
            max: xMax
          },
          y: {
            border: {
              display: true,
              color: 'black',
              width: NC.three
            },
            title: {
              display: true, 
              text: VC.viscositycp, 
              font: {
                size: NC.thirtyFive,
                weight: 'bold'
              },
              color: 'black'
            },
            grid: {
              display: false,
            },
            afterBuildTicks: (scale) => {  
              scale.ticks = [];  
              for (let v = yMin; v <= yMax + 0.0001; v += yStepSize) {    
                scale.ticks.push({      value: Number(v.toFixed(1))    });  
              }
            },
            ticks: {
              font: {
                weight: 'bold',
                size: NC.thirty
              },
              major: {
                enabled: true,
              },
              stepSize:yStepSize,
              color: 'black',
              callback: (value) => Number(value).toFixed(NC.one)
            },
            min: yMin,
            max: yMax
          }
        }
      },
      plugins: [this.boxBorderPlugin, this.axisOriginTicksPlugin]
    });
  }

  UpdateStatus(status: StatusParams) {
    this.statusChange$.next(status);
  }

  boxBorderPlugin = {
    id: 'boxBorderPlugin',
    beforeDraw: (chart: Chart) => {
      const ctx = chart.ctx;
      const chartArea = chart.chartArea;

      ctx.save();
      ctx.strokeStyle = 'black';
      ctx.lineWidth = NC.three;

      // Top border
      ctx.beginPath();
      ctx.moveTo(chartArea.left, chartArea.top);
      ctx.lineTo(chartArea.right, chartArea.top);
      ctx.stroke();

      // Right border
      ctx.lineWidth = NC.four;
      ctx.beginPath();
      ctx.moveTo(chartArea.right, chartArea.top);
      ctx.lineTo(chartArea.right, chartArea.bottom);
      ctx.stroke();

      ctx.restore();
    }
  };

  axisOriginTicksPlugin = {
    id: VC.axisOriginTicksPlugin,
    beforeDraw: (chart: Chart) => {
      const ctx = chart.ctx;
      const xScale = chart.scales['x'];
      const yScale = chart.scales['y'];
      const tickLength = NC.six;
      ctx.save();
      ctx.strokeStyle = 'black';
      ctx.lineWidth = NC.two;
      // Draw ticks on X axis (inside plot area only)
      xScale.ticks.forEach(tick => {
        const x = xScale.getPixelForValue(tick.value);
        const yEnd = chart.chartArea.bottom;

        if (x >= chart.chartArea.left && x <= chart.chartArea.right) {
          ctx.beginPath();
          ctx.moveTo(x, yEnd - tickLength); // bottom edge inward
          ctx.lineTo(x, yEnd);              // bottom edge
          ctx.stroke();
        }
      });

      // Draw ticks on Y axis (inside plot area only)
      yScale.ticks.forEach(tick => {
        const y = yScale.getPixelForValue(tick.value);
        const xStart = chart.chartArea.left;

        if (y >= chart.chartArea.top && y <= chart.chartArea.bottom) {
          ctx.beginPath();
          ctx.moveTo(xStart, y);              // left edge
          ctx.lineTo(xStart + tickLength, y); // left edge inward
          ctx.stroke();
        }
      });

      ctx.restore();

    }
  };

  GetSampleDetails(labTestId: number) {
    return this._http
      .get<APIResponse<SampleCreationModel>>(
        `${this.apiBaseUrl}${API_ENDPOINTS.GET_SAMPLE_DETAILS}/${labTestId}`
      )
      .pipe(
        map((resp) => {
          if (!resp?.isSuccess || !resp?.result) {
            throw new Error(resp?.displayMessage || 'Failed to load sample details to get FluidType in ViscosityService');
          }
          return resp.result;
        })
      );
  }

  ComputeReportAxisRanges(points: { x: number; y: number }[]) {
    // Fallback defaults (your previous fixed values)
    const defaults = { xMin: NC.zero, xMax: NC.sixteenThousand, yMin: NC.zero, yMax: NC.onePointSix };

    if (!points || points.length === NC.zero) return defaults;

    const xs = points.map(p => p.x);
    const ys = points.map(p => p.y);

    let xMin = Math.min(...xs);
    let xMax = Math.max(...xs);
    let yMin = Math.min(...ys);
    let yMax = Math.max(...ys);

    // Handle single-point / flat-line
    if (xMin === xMax) { xMin -= NC.one; xMax += NC.one; }
    if (yMin === yMax) { yMin -= NC.point05; yMax += NC.point05; }

    const xPad = Math.max(NC.hundred, NC.point05 * (xMax - xMin));
    const yPad = Math.max(NC.zeroPointTwo, NC.point05 * (yMax - yMin));

    xMin = Math.max(NC.zero, xMin - xPad);
    xMax = xMax + xPad;
    yMin = Math.max(NC.zero, yMin - yPad);
    yMax = yMax + yPad;

    // Left-padding fix so the first point isn't drawn on top of the Y-axis
    if (xMin === NC.zero) {
      xMin = NC.twoHundredNegative; // small negative margin is OK; ticks remain clean after rounding
    }

    xMin = Math.floor(xMin / NC.thousand) * NC.thousand;
    xMax = Math.ceil(xMax / NC.thousand) * NC.thousand;
    yMin = Number(yMin.toFixed(NC.two));
    yMax = Number(yMax.toFixed(NC.two));

    // don't collapse if rounding made min === max
    if (xMax <= xMin) xMax = xMin + NC.thousand;
    if (yMax <= yMin) yMax = yMin + NC.zeroPointOne;

    return { xMin, xMax, yMin, yMax };
  }

  // ── State / Business Logic (merged from viscosity-state.service) ────────────

  ComputeAxisRanges(points: { x: number; y: number }[]): { xMin: number; xMax: number; yMin: number; yMax: number } {
    if (!points || points.length === NC.zero) {
      return { xMin: NC.zero, xMax: NC.fifteenThousand, yMin: NC.zero, yMax: NC.one };
    }

    const xs = points.map(p => p.x);
    const ys = points.map(p => p.y);

    let xMin = Math.min(...xs);
    let xMax = Math.max(...xs);
    let yMin = Math.min(...ys);
    let yMax = Math.max(...ys);

    if (xMin === xMax) { xMin -= NC.one; xMax += NC.one; }
    if (yMin === yMax) { yMin -= NC.point05; yMax += NC.point05; }

    const xPad = Math.max(NC.one, NC.point05 * (xMax - xMin));
    const yPad = Math.max(NC.point1, NC.point05 * (yMax - yMin));

    xMin = Math.max(NC.zero, xMin - xPad);
    xMax = xMax + xPad;
    yMin = Math.max(NC.zero, yMin - yPad);
    yMax = yMax + yPad;

    if (xMin === NC.zero) {
      xMin = NC.twoHundredNegative;
    }

    xMin = Math.floor(xMin / NC.thousand) * NC.thousand;
    xMax = Math.ceil(xMax / NC.thousand) * NC.thousand;
    yMin = Number(yMin.toFixed(NC.two));
    yMax = Number(yMax.toFixed(NC.two));

    return { xMin, xMax, yMin, yMax };
  }

  ToNumberOrNull(v: unknown): number | null {
    if (v === null || v === undefined) return null;
    const s = String(v).trim();
    if (s === '') return null;
    const n = Number(s);
    return Number.isFinite(n) ? n : null;
  }

  ResolveAxisUpdateValues(
    input: {
      xMin: unknown;
      xMax: unknown;
      yMin: unknown;
      yMax: unknown;
      xStep: unknown;
      yStep: unknown;
    },
    pressurePCVChart: Chart | null
  ): {
    xMin: number;
    xMax: number;
    yMin: number;
    yMax: number;
    xStep?: number;
    yStep?: number;
  } {
    const xMin = this.ToNumberOrNull(input.xMin) ?? pressurePCVChart?.options?.scales?.['x']?.min as number;
    const xMax = this.ToNumberOrNull(input.xMax) ?? pressurePCVChart?.options?.scales?.['x']?.max as number;
    const yMin = this.ToNumberOrNull(input.yMin) ?? pressurePCVChart?.options?.scales?.['y']?.min as number;
    const yMax = this.ToNumberOrNull(input.yMax) ?? pressurePCVChart?.options?.scales?.['y']?.max as number;
    const xStep = this.ToNumberOrNull(input.xStep) ?? undefined;
    const yStep = this.ToNumberOrNull(input.yStep) ?? undefined;
    return { xMin, xMax, yMin, yMax, xStep, yStep };
  }

  GetPressureValues(data: ViscosityData[]): number[] {
    return data
      .map(d => d.pressure)
      .filter((p): p is number => p !== null);
  }

  NormalizePressuresForChart(params: {
    saturationPressure: number | null;
    reservoirPressure: number | null;
    pressureValues: number[];
  }): { saturationPressure: number | null; reservoirPressure: number | null; canProceed: boolean } {
    let saturationPressure = params.saturationPressure;
    let reservoirPressure = params.reservoirPressure;

    if (
      saturationPressure !== null &&
      saturationPressure !== undefined &&
      !isNaN(Number(saturationPressure))
    ) {
      const nearestSatP = this.GetNearestPressure(saturationPressure, params.pressureValues, VC.saturationPressure);
      if (nearestSatP !== null && nearestSatP !== saturationPressure) {
        saturationPressure = nearestSatP;
      }
    }

    if (
      reservoirPressure !== null &&
      reservoirPressure !== undefined &&
      !isNaN(Number(reservoirPressure))
    ) {
      const nearestResP = this.GetNearestPressure(
        reservoirPressure,
        params.pressureValues,
        VC.reservoirPressure,
        saturationPressure ?? undefined
      );

      if (nearestResP === null) {
        return { saturationPressure, reservoirPressure, canProceed: false };
      }

      if (nearestResP !== null && nearestResP !== saturationPressure) {
        reservoirPressure = nearestResP;
      }
    }

    return { saturationPressure, reservoirPressure, canProceed: true };
  }

  NormalizePressuresForSave(params: {
    saturationPressure: number | null;
    reservoirPressure: number | null;
    pressureValues: number[];
  }): { saturationPressure: number | null; reservoirPressure: number | null } {
    let saturationPressure = params.saturationPressure;
    let reservoirPressure = params.reservoirPressure;

    if (saturationPressure !== null) {
      const nearestSatP = this.GetNearestPressure(saturationPressure, params.pressureValues, VC.saturationPressure);
      if (nearestSatP !== null && nearestSatP !== saturationPressure) {
        saturationPressure = nearestSatP;
      }
    }

    if (saturationPressure !== null && reservoirPressure !== null) {
      const nearestResP = this.GetNearestPressure(
        reservoirPressure,
        params.pressureValues,
        VC.reservoirPressure,
        saturationPressure
      );
      if (nearestResP !== null && nearestResP !== reservoirPressure) {
        reservoirPressure = nearestResP;
      }
    }

    return { saturationPressure, reservoirPressure };
  }

  HasAtLeastOneValidViscosityRow(rows: ViscosityData[]): boolean {
    return rows.some(r => {
      const temp = r?.temperature;
      const pressure = r?.pressure;
      const pcv = r?.pcv;

      const tempOk = temp !== null && temp !== undefined && String(temp).trim() !== '' && Number.isFinite(Number(temp));
      const pressureOk = pressure !== null && pressure !== undefined && String(pressure).trim() !== '' && Number.isFinite(Number(pressure));
      const pcvOk = pcv !== null && pcv !== undefined && String(pcv).trim() !== '' && Number.isFinite(Number(pcv));

      return tempOk && pressureOk && pcvOk;
    });
  }

  IsFinishExecutionDisabled(params: {
    rows: ViscosityData[];
    saturationPressure: number | null;
    reservoirPressure: number | null;
  }): boolean {
    const hasAtLeastOneValidRow = this.HasAtLeastOneValidViscosityRow(params.rows);

    const satFilled =
      params.saturationPressure !== null &&
      params.saturationPressure !== undefined &&
      String(params.saturationPressure).trim() !== '';

    const resFilled =
      params.reservoirPressure !== null &&
      params.reservoirPressure !== undefined &&
      String(params.reservoirPressure).trim() !== '';

    return !hasAtLeastOneValidRow || !satFilled || !resFilled;
  }

  IsUpdateResultDisabled(isFinishExecutionClicked: boolean, isEmvViscosityEdited: boolean, isResultEdited: boolean): boolean {
    if (isFinishExecutionClicked) return true;
    return !(isEmvViscosityEdited || isResultEdited);
  }

  GetNearestPressure(inputPressure: number | null, pressureValues: number[], fieldName: string, minExclusive?: number): number | null {
    if (inputPressure === null || Number.isNaN(inputPressure) || pressureValues.length === NC.zero) {
      return null;
    }

    const inputPressureNum = Number(inputPressure);
    let validPressures = pressureValues.map(p => Number(p));

    if (minExclusive !== undefined) {
      validPressures = validPressures.filter(p => p > minExclusive);
      if (validPressures.length === NC.zero) {
        this.toastr.error(`No ${fieldName} value in grid is greater than ${minExclusive}`);
        return null;
      }
    }

    const maxPressure = Math.max(...validPressures);
    if (inputPressureNum > maxPressure) {
      return null;
    }

    const exactMatch = validPressures.includes(inputPressureNum);
    if (exactMatch) {
      return inputPressureNum;
    }

    const nearest = validPressures.reduce((prev, curr) =>
      Math.abs(curr - inputPressureNum) < Math.abs(prev - inputPressureNum) ? curr : prev
    );
    this.toastr.warning(`Entered ${fieldName} not found. Using nearest value: ${nearest}`);
    return nearest;
  }

  BuildViscosityPayload(params: {
    viscosityData: ViscosityData[];
    userId: string;
    role: string;
    labRepeatId: number;
    notes?: string;
    reservoirPressure: number | null;
    saturationPressure: number | null;
    rangeFrom: number | null;
    rangeTo: number | null;
    slope: number | null | undefined;
    intercept: number | null | undefined;
    fluidType: string;
  }): EMVViscosityData {
    const rangeFromValue = (params.fluidType.toLowerCase() === CC.oil) ? (params.rangeFrom! * NC.thousand) : null;
    const rangeToValue = (params.fluidType.toLowerCase() === CC.oil) ? (params.rangeTo! * NC.thousand) : null;
    return {
      viscosityData: params.viscosityData,
      userId: params.userId,
      labRepeatId: Number(params.labRepeatId),
      notes: params.notes,
      resP: params.reservoirPressure ?? null,
      satP: params.saturationPressure ?? NC.zero,
      rangeFrom: rangeFromValue,
      rangeTo: rangeToValue,
      slope: params.slope ? params.slope : null,
      intercept: params.intercept ? params.intercept : null,
      fluidType: params.fluidType,
      role: params.role
    };
  }

  CheckVolumeRange(label: string, min: number, max: number, i: number, value: unknown): boolean {
    if ((value !== undefined && value !== null && value !== '') &&
      (Number(value) < min || Number(value) > max)) {
      this.toastr.warning(label + (i + NC.one));
      return true;
    }
    return false;
  }

  RunRangeValidation(data: ViscosityData[]): boolean {
    let hasOutOfRange = false;
    for (let i = NC.zero; i < data.length; i++) {
      if (this.CheckVolumeRange('Temperature out of range (50-350) at row ', NC.fifty, NC.threeFifty, i, data[i].temperature)) hasOutOfRange = true;
      if (this.CheckVolumeRange('Pressure out of range (0-15000) at row ', NC.zero, NC.fifteenThousand, i, data[i].pressure)) hasOutOfRange = true;
      if (this.CheckVolumeRange('PCV out of range (0-50) at row ', NC.zero, NC.fifty, i, data[i].pcv)) hasOutOfRange = true;
    }
    return hasOutOfRange;
  }

  ValidateViscosityRows(data: ViscosityData[]): { isValid: boolean; hasError: boolean } {
    const hasInvalidRow = data.some(r =>
      r.temperature === null || r.pressure === null || r.pcv === null
    );
    if (hasInvalidRow) {
      this.toastr.error(MC.oneOrMoreRowsAreEmptyPleaseEnterAllRequiredData);
      return { isValid: false, hasError: true };
    }
    return { isValid: true, hasError: false };
  }

  ValidateEmvRowsForTab(data: ViscosityData[], state: EmvOutOfRangeState): EmvValidationResult {
    const invalidFieldMap = new Map<string, boolean>();
    const outOfRangeMessages: string[] = [];
    const outOfRangeKeys: string[] = [];
    const errorMessages: string[] = [];
    let hasValidRow = false;

    const confirmedOutOfRangeCells = new Map(state.confirmedOutOfRangeCells);
    const lastConfirmedOutOfRangeValue = new Map(state.lastConfirmedOutOfRangeValue);

    for (let i = NC.zero; i < data.length; i++) {
      const row = data[i];

      const rawTemp = row.temperature ?? null;
      const rawPressure = row.pressure ?? null;
      const rawPCV = row.pcv ?? null;

      const temp = Number(rawTemp);
      const pressure = Number(rawPressure);
      const pcv = Number(rawPCV);

      const isEmpty =
        rawTemp === null &&
        rawPressure === null &&
        rawPCV === null &&
        (!row.comments || row.comments.trim() === PC.emptyString);

      if (isEmpty) {
        errorMessages.push(`Row ${i + NC.one} is empty. Please enter data.`);
        continue;
      }

      const isFullyFilled =
        rawTemp !== null && !isNaN(temp) &&
        rawPressure !== null && !isNaN(pressure) &&
        rawPCV !== null && !isNaN(pcv);

      if (!isFullyFilled) {
        if (rawTemp === null || isNaN(temp)) {
          errorMessages.push(`${MC.enterValueForTemperatureForRowNumber} ${i + NC.one}`);
          invalidFieldMap.set(`${CC.temperature3}${i}`, true);
        }
        if (rawPressure === null || isNaN(pressure)) {
          errorMessages.push(`${MC.enterValueForPressureForRowNumber} ${i + NC.one}`);
          invalidFieldMap.set(`${CC.pressure3}${i}`, true);
        }
        if (rawPCV === null || isNaN(pcv)) {
          errorMessages.push(`${MC.enterValueForPcvForRowNumber} ${i + NC.one}`);
          invalidFieldMap.set(`${VC.pcvPrefix}${i}`, true);
        }
        continue;
      }

      hasValidRow = true;

      const cellKeyTemp = VC.temp(i);
      const cellKeyPressure = VC.pressure(i);
      const cellKeyPCV = VC.pcvTwo(i);

      const lastConfirmedTemp = lastConfirmedOutOfRangeValue.get(cellKeyTemp);
      if (temp < NC.fifty || temp > NC.threeFifty) {
        if (confirmedOutOfRangeCells.get(cellKeyTemp) || lastConfirmedTemp !== temp) {
          outOfRangeMessages.push(`Temperature out of range (50-350) at row ${i + NC.one}`);
          outOfRangeKeys.push(cellKeyTemp);
          confirmedOutOfRangeCells.delete(cellKeyTemp);
          lastConfirmedOutOfRangeValue.delete(cellKeyTemp);
        }
      } else {
        confirmedOutOfRangeCells.delete(cellKeyTemp);
        lastConfirmedOutOfRangeValue.delete(cellKeyTemp);
      }

      const lastConfirmedPressure = lastConfirmedOutOfRangeValue.get(cellKeyPressure);
      if (pressure < NC.zero || pressure > NC.fifteenThousand) {
        if (confirmedOutOfRangeCells.get(cellKeyPressure) || lastConfirmedPressure !== pressure) {
          outOfRangeMessages.push(`Pressure out of range (0-15000) at row ${i + NC.one}`);
          outOfRangeKeys.push(cellKeyPressure);
          confirmedOutOfRangeCells.delete(cellKeyPressure);
          lastConfirmedOutOfRangeValue.delete(cellKeyPressure);
        }
      } else {
        confirmedOutOfRangeCells.delete(cellKeyPressure);
        lastConfirmedOutOfRangeValue.delete(cellKeyPressure);
      }

      const lastConfirmedPCV = lastConfirmedOutOfRangeValue.get(cellKeyPCV);
      if (pcv < NC.zero || pcv > NC.fifty) {
        if (confirmedOutOfRangeCells.get(cellKeyPCV) || lastConfirmedPCV !== pcv) {
          outOfRangeMessages.push(`PCV out of range (0-50) at row ${i + NC.one}`);
          outOfRangeKeys.push(cellKeyPCV);
          confirmedOutOfRangeCells.delete(cellKeyPCV);
          lastConfirmedOutOfRangeValue.delete(cellKeyPCV);
        }
      } else {
        confirmedOutOfRangeCells.delete(cellKeyPCV);
        lastConfirmedOutOfRangeValue.delete(cellKeyPCV);
      }
    }

    return {
      invalidFieldMap,
      errorMessages,
      outOfRangeMessages,
      outOfRangeKeys,
      hasValidRow,
      state: {
        confirmedOutOfRangeCells,
        lastConfirmedOutOfRangeValue,
      }
    };
  }

  ApplyEmvOutOfRangeConfirmation(
    data: ViscosityData[],
    outOfRangeKeys: string[],
    proceed: boolean,
    state: EmvOutOfRangeState
  ): EmvOutOfRangeState {
    const confirmedOutOfRangeCells = new Map(state.confirmedOutOfRangeCells);
    const lastConfirmedOutOfRangeValue = new Map(state.lastConfirmedOutOfRangeValue);

    outOfRangeKeys.forEach(key => {
      confirmedOutOfRangeCells.set(key, proceed);
      const [rowIdx, col] = key.split('-');
      const row = data[+rowIdx];
      let value = NaN;
      if (col === CC.pressure) value = Number(row.pressure);
      if (col === CC.temperature2) value = Number(row.temperature);
      if (col === VC.pcv) value = Number(row.pcv);
      lastConfirmedOutOfRangeValue.set(key, value);
    });

    return {
      confirmedOutOfRangeCells,
      lastConfirmedOutOfRangeValue,
    };
  }

  ValidatePressureInputs(params: {
    satPressure: number | null;
    resPressure: number | null;
    rangeFrom: number | null;
    rangeTo: number | null;
    pressureValues: number[];
  }): { isValid: boolean; hasWarnings: boolean; invalidFields: Map<string, boolean> } {
    const { satPressure, resPressure, rangeFrom, rangeTo, pressureValues } = params;
    const errorMessages: string[] = [];
    const warningMessages: string[] = [];
    const invalidFields = new Map<string, boolean>();

    if (satPressure === null || satPressure === undefined || Number.isNaN(satPressure)) {
      errorMessages.push(MC.saturationPressureIsRequired);
      invalidFields.set(VC.satP, true);
    }

    const resPressureNum = typeof resPressure === 'string' ? Number(resPressure) : resPressure;
    if (resPressureNum === null || Number.isNaN(resPressureNum)) {
      errorMessages.push(MC.reservoirPressureIsRequired);
      invalidFields.set(VC.resP, true);
    }

    if (errorMessages.length > NC.zero) {
      errorMessages.forEach(msg => this.toastr.error(msg));
      return { isValid: false, hasWarnings: false, invalidFields };
    }

    if (pressureValues.length === NC.zero) {
      return { isValid: true, hasWarnings: false, invalidFields };
    }

    const maxPressure = Math.max(...pressureValues);

    if (satPressure !== null && satPressure > maxPressure) {
      errorMessages.push(`Saturation Pressure cannot exceed max grid pressure (${maxPressure})`);
      invalidFields.set(VC.satP, true);
    }
    if (resPressure && resPressure > maxPressure) {
      errorMessages.push(`Reservoir Pressure cannot exceed max grid pressure (${maxPressure})`);
      invalidFields.set(VC.resP, true);
    }
    if (resPressure && satPressure !== null && resPressure <= satPressure) {
      errorMessages.push('Reservoir Pressure must be greater than Saturation Pressure');
      invalidFields.set(VC.resP, true);
    }

    if (rangeFrom && (rangeFrom < NC.ten || rangeFrom > NC.fourteen)) {
      warningMessages.push('Range From value must be between 10 and 14');
      invalidFields.set(VC.rangeFrom, true);
    }
    if (rangeTo && (rangeTo < NC.ten || rangeTo > NC.fourteen)) {
      warningMessages.push('Range To value must be between 10 and 14');
      invalidFields.set(VC.rangeTo, true);
    }
    if (rangeFrom && rangeTo && (rangeFrom > rangeTo)) {
      warningMessages.push(MC.rangeFromValueCannotBeGreaterThanRangeToValue);
      invalidFields.set(VC.range, true);
    }

    errorMessages.forEach(msg => this.toastr.error(msg));
    warningMessages.forEach(msg => this.toastr.warning(msg));

    return {
      isValid: errorMessages.length === NC.zero,
      hasWarnings: warningMessages.length > NC.zero,
      invalidFields
    };
  }

  IsNumeric(val: unknown): boolean {
    return !isNaN(parseFloat(String(val))) && isFinite(Number(val));
  }

  CreateEmptyRow(): ViscosityData {
    return {
      temperature: null,
      pressure: null,
      pcv: null,
      expectedPCV: null,
      comments: PC.emptyString,
    };
  }

  GetMinValue(col: string): number | null {
    switch (col) {
      case CC.temperature2: return NC.fifty;
      case CC.pressure: return NC.zero;
      case VC.pcv: return NC.zero;
      default: return null;
    }
  }

  GetMaxValue(col: string): number | null {
    switch (col) {
      case CC.temperature2: return NC.threeFifty;
      case CC.pressure: return NC.fifteenThousand;
      case VC.pcv: return NC.fifty;
      default: return null;
    }
  }

  ConvertExcelRowsToViscosityData(excelData: Record<string, unknown>[], fluidType: string): ViscosityData[] {
    const convertedData: ViscosityData[] = [];
    excelData.forEach((row: Record<string, unknown>) => {
      const rawTemp = String(row[VC.tempF] ?? PC.emptyString).replace(RC.NON_NUMERIC_EXCEPT_DOT_AND_MINUS, PC.emptyString);
      const rawPressure = String(row[CC.pressurePsia] ?? PC.emptyString).replace(RC.NON_NUMERIC_EXCEPT_DOT_AND_MINUS, PC.emptyString);
      const rawPCV = String(row[VC.pcvcp] ?? PC.emptyString).replace(RC.NON_NUMERIC_EXCEPT_DOT_AND_MINUS, PC.emptyString);
      const rawExpectedPCV = fluidType.toLowerCase() === CC.oil && VC.expectedPCVTwo in row
        ? String(row[VC.expectedPCVTwo] ?? PC.emptyString).replace(RC.NON_NUMERIC_EXCEPT_DOT_AND_MINUS, PC.emptyString)
        : null;
      const entry: ViscosityData = {
        temperature: rawTemp === PC.emptyString ? null : Number(rawTemp),
        pressure: rawPressure === PC.emptyString ? null : Number(rawPressure),
        pcv: rawPCV === PC.emptyString ? null : Number(rawPCV),
        expectedPCV: rawExpectedPCV === null || rawExpectedPCV === PC.emptyString ? null : Number(rawExpectedPCV),
        comments: String(row[VC.commentsTwo] ?? PC.emptyString)
      };
      convertedData.push(entry);
    });
    return convertedData;
  }

  /**
   * Builds chart datasets for viscosity chart based on fluid type and sorted data
   * Separates data by saturation pressure for gas type, or by pressure relationship for oil type
   */
  BuildChartDatasets(
    sortedData: ViscosityData[],
    saturationPoint: number,
    fluidType: string
  ): {
    datasets: ChartDataset<'line', { x: number; y: number }[]>[];
    plottedPoints: { x: number; y: number }[];
  } {
    let datasets: ChartDataset<'line', { x: number; y: number }[]>[] = [];
    let plottedPoints: { x: number; y: number }[] = [];

    if (fluidType.toLowerCase() === VC.gas) {
      const belowSatP = sortedData
        .filter(d => d.pressure !== null && d.pressure < saturationPoint)
        .map(d => ({ x: d.pressure!, y: d.pcv ?? NC.zero }));
      const aboveSatP = sortedData
        .filter(d => d.pressure !== null && d.pressure >= saturationPoint)
        .map(d => ({ x: d.pressure!, y: d.pcv ?? NC.zero }));

      plottedPoints = [...belowSatP, ...aboveSatP];

      datasets = [
        {
          label: VC.belowSatP,
          data: belowSatP,
          borderColor: 'gray',
          backgroundColor: 'gray',
          pointRadius: NC.four,
          pointStyle: 'circle',
          showLine: true,
          fill: false,
          tension: NC.zeroPointOne,
          datalabels: { display: false, formatter: () => '' }
        },
        {
          label: VC.aboveSatP,
          data: aboveSatP,
          borderColor: 'orange',
          backgroundColor: 'orange',
          pointRadius: NC.four,
          pointStyle: 'circle',
          showLine: true,
          fill: false,
          tension: NC.zeroPointOne,
          datalabels: { display: false, formatter: () => '' }
        }
      ];
    }
    else if (fluidType.toLowerCase() === CC.oil) {
      const allPoints = sortedData
        .filter(d => d.pressure !== null)
        .map(d => ({ x: d.pressure!, y: d.pcv ?? NC.zero }));

      plottedPoints = allPoints;

      datasets = [
        {
          label: VC.pcvPressure,
          data: allPoints,
          borderColor: 'gray',
          backgroundColor: 'transparent',
          pointRadius: NC.four,
          pointStyle: 'circle',
          showLine: true,
          fill: false,
          tension: NC.zeroPointOne,
          segment: {
            borderColor: (ctx: ScriptableLineSegmentContext) => {
              const index = ctx.p0DataIndex;
              const pressure = allPoints[index]?.x ?? NC.zero;
              return pressure <= saturationPoint ? 'gray' : 'orange';
            }
          },
          pointBackgroundColor: (ctx: ScriptableContext<'line'>) => {
            const index = ctx.dataIndex;
            const pressure = allPoints[index]?.x ?? NC.zero;
            return pressure <= saturationPoint ? 'gray' : 'orange';
          },
          pointBorderColor: (ctx: ScriptableContext<'line'>) => {
            const index = ctx.dataIndex;
            const pressure = allPoints[index]?.x ?? NC.zero;
            return pressure <= saturationPoint ? 'gray' : 'orange';
          },
          datalabels: { display: false, formatter: () => '' }
        }
      ];
    }

    return { datasets, plottedPoints };
  }
}
